/**
 * Pièces communes à la liste des sprints et à la page d'UN sprint (SRF-PRJ-040/041) : badge de
 * statut, adresse de la page d'un sprint, erreur de la passerelle dite avec son code nommé.
 */
import type { JSX } from 'react'

import { ApiError, type SprintStatus } from '@/api/client'
import { codeErreur } from '@/api/niveauxApi'
import { Badge } from '@/components/ui/badge'
import { useT } from '@/i18n/language'

/** Adresse de la page d'un sprint : une vue du projet, l'identifiant dans l'adresse. */
export function lienSprint(id: number): string {
  return `/projet?vue=sprints&sprint=${id}`
}

/** Liste des sprints du projet. */
export const LIEN_TOUS_LES_SPRINTS = '/projet?vue=sprints'

// Accent de couleur des statuts actif/clôturé — tokens de tons sémantiques (G6), theme- et
// contraste-safe ; le libellé porte toujours l'information (jamais la couleur seule).
const ACCENT: Partial<Record<SprintStatus, { border: string; text: string }>> = {
  active: { border: 'var(--color-info)', text: 'var(--color-info-text)' },
  closed: { border: 'var(--color-success)', text: 'var(--color-success-text)' },
}

export function useLibellesStatutSprint(): Record<SprintStatus, string> {
  const { t } = useT()
  return {
    planned: t('sprints.statusPlanned', 'Planifié'),
    active: t('sprints.statusActive', 'Actif'),
    closed: t('sprints.statusClosed', 'Clôturé'),
  }
}

export function BadgeStatutSprint({ status, testid }: { status: SprintStatus; testid?: string }): JSX.Element {
  const libelles = useLibellesStatutSprint()
  const accent = ACCENT[status]
  return (
    <Badge variant="outline" data-testid={testid} data-statut={status}
           className={status === 'planned' ? 'text-muted-foreground' : ''}
           style={accent ? { borderColor: accent.border, color: accent.text } : undefined}>
      {libelles[status]}
    </Badge>
  )
}

/** Une erreur de la passerelle : son message, et son code NOMMÉ dans un « Détail technique ». */
export function ErreurNommee({ erreur, testid }: { erreur: unknown; testid?: string }): JSX.Element {
  const { t } = useT()
  const code = codeErreur(erreur)
  const message = erreur instanceof ApiError || erreur instanceof Error
    ? erreur.message
    : t('sprint.erreurInconnue', 'Erreur inattendue')
  return (
    <div role="alert" data-testid={testid} data-code={code ?? undefined} className="space-y-1 text-sm text-destructive">
      <p>{message}</p>
      {code ? (
        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer">{t('arret.detailTechnique', 'Détail technique')}</summary>
          <code>{code}</code>
        </details>
      ) : null}
    </div>
  )
}
